if (!customElements.get('search-drawer')) {
  customElements.define(
    'search-drawer',
    class SearchDrawer extends DrawerElement {
      constructor() {
        super();
      }

      get shouldAppendToBody() {
        return false;
      }

      get input() {
        return this.querySelector('input[type="search"]');
      }

      get focusElement() {
        return this.querySelector('input[type="search"]');
      }
    }
  );
}

if (!customElements.get('search-typed')) {
  customElements.define(
    'search-typed',
    class SearchTyped extends HTMLElement {
      constructor() {
        super();

        Motion.inView(this, this.init.bind(this));
      }

      get startDelay() {
        return this.hasAttribute('data-delay') ? parseFloat(this.getAttribute('data-delay')) : 0;
      }

      async init() {
        this.insertCursor();
        await this.start(this.getAttribute('data-first-text'), this.startDelay);

        setTimeout(async () => {
          await this.reset();
          await this.start(this.getAttribute('data-last-text'), 0);
        }, 600);
      }

      async start(text, delay) {
        this.innerHTML = text;
        await Motion.animate(this, { width: [0, `${this.scrollWidth}px`] }, { duration: 1, delay }).finished;
        this.cursor.classList.add('blink');
      }

      async reset() {
        this.cursor.classList.remove('blink');
        await Motion.animate(this, { width: 0 }, { duration: 0.25 }).finished;
      }

      insertCursor() {
        if (this.cursor) return;

        this.cursor = document.createElement('span');
        this.cursor.className = 'typed-cursor';
        this.cursor.setAttribute('aria-hidden', true);
        this.cursor.innerHTML = '|';
        this.parentElement.insertBefore(this.cursor, this.nextSibling);
      }
    }
  );
}

// Search product titles across the live catalogue before using broad suggestions.
customElements.get("predictive-search") || customElements.define("predictive-search", class extends HTMLFormElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    this.focusElement = this.input;
    const page = new URL(window.location.href);
    if (this.input.value === page.searchParams.get("q") && page.searchParams.has("om_q")) {
      this.input.value = page.searchParams.get("om_q");
    }
    this.resetButton.addEventListener("click", this.clear.bind(this));
    this.input.addEventListener("click", this.hideTypewriter.bind(this));
    this.input.addEventListener("input", () => this.abortController?.abort());
    this.input.addEventListener("input", theme.utils.debounce(this.onChange.bind(this), 300));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("submit", this.onSubmit.bind(this));
  }
  get input() { return this.querySelector('input[type="search"]'); }
  get resetButton() { return this.querySelector('button[type="reset"]'); }
  get typewriter() { return this.input.previousElementSibling; }
  get resultId() { return "PredictiveSearchResults-" + theme.utils.sectionId(this); }
  get limit() { return Math.max(1, Math.min(10, parseInt(this.getAttribute("data-limit"), 10) || 3)); }
  hideTypewriter() { this.typewriter?.setAttribute("hidden", ""); }
  onFocus() {
    if (this.closest(".collection")) {
      document.body.classList.add("predictive-search-open");
      if (this.getQuery()) this.onChange();
    }
  }
  clear(event) {
    event?.preventDefault();
    this.abortController?.abort();
    this.input.value = "";
    this.removeAttribute("loading");
    this.removeAttribute("results");
    this.input.focus();
  }
  getQuery() { return this.input.value.trim(); }
  titleQuery(query) {
    // Only literal words enter Shopify's search syntax; punctuation cannot inject operators.
    const terms = query.match(/[\p{L}\p{N}]+(?:[/.][\p{N}]+)*/gu) || [];
    return terms.length && terms.length <= 32 ? terms.map(term => 'title:"' + term + '"').join(" AND ") : "";
  }
  normalise(value) {
    return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  }
  buildUrl(query) {
    const url = new URL(`${theme.routes.shop_url}${theme.routes.predictive_search_url}`);
    url.searchParams.set("q", query);
    url.searchParams.set("resources[limit]", this.limit);
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("section_id", theme.utils.sectionId(this));
    return url;
  }
  searchUrl(query, titleQuery = "") {
    const url = new URL(this.action, window.location.href);
    url.search = new URLSearchParams(new FormData(this)).toString();
    url.searchParams.set("q", titleQuery || query);
    url.searchParams.delete("section_id");
    if (titleQuery) {
      url.searchParams.set("type", "product");
      url.searchParams.set("om_q", query);
    } else {
      url.searchParams.delete("om_q");
    }
    return url;
  }
  async fetchSection(url, signal) {
    const response = await fetch(url, { signal });
    if (!response.ok) throw new Error("Search response " + response.status);
    const doc = new DOMParser().parseFromString(await response.text(), "text/html");
    const section = doc.getElementById(this.resultId);
    if (!section) throw new Error("Search section unavailable");
    return section;
  }
  async getResults(query, signal) {
    if (this.cachedMap.has(query)) return this.cachedMap.get(query);
    const titleQuery = this.titleQuery(query);
    const strictUrl = this.searchUrl(query, titleQuery);
    strictUrl.searchParams.set("section_id", theme.utils.sectionId(this));
    const [native, strict] = await Promise.allSettled([
      this.fetchSection(this.buildUrl(query), signal),
      titleQuery ? this.fetchSection(strictUrl, signal) : Promise.resolve(null)
    ]);
    if (signal?.aborted) throw new DOMException("Search cancelled", "AbortError");
    const strictSection = strict.status === "fulfilled" ? strict.value : null;
    const hasTitles = !!strictSection?.querySelector(".horizontal-product");
    const section = hasTitles ? strictSection : (native.status === "fulfilled" ? native.value : null);
    if (!section) throw new Error("Search is temporarily unavailable");
    const products = section.querySelector(".horizontal-products");
    if (products) {
      const seen = new Set();
      const cards = Array.from(products.children).filter(card => {
        const link = card.querySelector(".horizontal-product__title");
        if (!link) return false;
        const key = new URL(link.href, window.location.origin).pathname;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      const queryKey = this.normalise(query);
      cards.sort((a, b) => Number(this.normalise(b.querySelector(".horizontal-product__title").textContent) === queryKey) - Number(this.normalise(a.querySelector(".horizontal-product__title").textContent) === queryKey));
      products.replaceChildren(...cards.slice(0, this.limit));
    }
    const result = { html: section.innerHTML, titleQuery: hasTitles ? titleQuery : "" };
    if (this.cachedMap.size >= 50) this.cachedMap.delete(this.cachedMap.keys().next().value);
    this.cachedMap.set(query, result);
    return result;
  }
  async onChange() {
    const query = this.getQuery();
    if (!query) { this.clear(); return; }
    this.hideTypewriter();
    this.abortController?.abort();
    const controller = this.abortController = new AbortController();
    this.setAttribute("loading", "");
    try {
      const result = await this.getResults(query, controller.signal);
      if (this.getQuery() !== query || controller.signal.aborted) return;
      const target = document.getElementById(this.resultId);
      if (target) target.innerHTML = result.html;
      this.setAttribute("results", "");
    } catch (error) {
      if (error.name !== "AbortError") this.removeAttribute("results");
    } finally {
      if (this.abortController === controller) this.removeAttribute("loading");
    }
  }
  async onSubmit(event) {
    event.preventDefault();
    const query = this.getQuery();
    if (!query || this.submitting) return;
    this.submitting = true;
    this.abortController?.abort();
    const controller = this.abortController = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const result = await this.getResults(query, controller.signal);
      if (this.getQuery() === query) window.location.assign(this.searchUrl(query, result.titleQuery));
    } catch (error) {
      // A network failure must never prevent a normal search submission.
      if (this.getQuery() === query) window.location.assign(this.searchUrl(query));
    } finally {
      clearTimeout(timeout);
      this.submitting = false;
    }
  }
}, { extends: "form" });

if (!customElements.get('predictive-search-overlay')) {
  customElements.define(
    'predictive-search-overlay',
    class PredictiveSearchOverlay extends OverlayElement {
      constructor() {
        super();

        this.addEventListener('click', this.onClick);
      }

      onClick() {
        setTimeout(() => {
          document.body.classList.remove('predictive-search-open');
        });
      }
    }
  );
}
